import { LinkIcon, TriangleAlertIcon, XIcon } from 'lucide-react';
import { useEffect, useRef, useState, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import { DialogClose } from '@/components/ui/dialog.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import { Switch } from '@/components/ui/switch.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import type { AutomationSchedule } from '@/integration/types.js';
import { useCreateAutomation } from '@/lib/api/automations.js';
import { notify } from '@/lib/notify.js';

import { AutomationPromptField } from './AutomationPromptField.js';
import {
  AGENT_CHAT_CAPABILITY,
  type AutomationAttachment,
} from './automation-attachments.js';
import {
  FieldError,
  fieldErrorId,
  focusFirstInvalid,
  PROMPT_REQUIRED,
  RUN_LIMIT_INVALID,
} from './field-error.js';
import {
  SchedulePicker,
  type SchedulePickerHandle,
  type SavableSchedule,
} from './schedule-picker.js';
import { useAutomationSkillsField } from './useAutomationSkillsField.js';
import { useLatest } from './use-latest.js';
import {
  AUTOMATION_PRESETS,
  DEFAULT_PRESET,
  LINKEDIN_PRESET_ID,
  presetById,
} from './presets.js';
import { browserTz } from './tz.js';

/** A URL on linkedin.com (any subdomain). Used to gate the LinkedIn link editor. */
export function isLinkedInUrl(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return false;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
  return (
    url.hostname === 'linkedin.com' || url.hostname.endsWith('.linkedin.com')
  );
}

// The "Watch LinkedIn" type composes its final prompt from the base text plus the attached
// links — `<base>\n\nlinks:\n- <url>` (one line per url). Other types send the prompt as-is.
/** Shown when a save finds the Watch LinkedIn type with no link attached. */
const LINK_REQUIRED = 'Add at least one linkedin.com link.';

const PROMPT_FIELD_ID = 'create-automation-prompt';
const LINK_FIELD_ID = 'create-automation-linkedin-url';
const RUN_LIMIT_FIELD_ID = 'create-automation-max-fires';

/** Where a click on Create is: idle, waiting for the schedule to be interpreted, or saving. */
type CreatePhase = 'idle' | 'checking' | 'saving';

export function composeLinkedInPrompt(
  base: string,
  links: readonly string[],
): string {
  const trimmed = base.trim();
  if (links.length === 0) return trimmed;
  return `${trimmed}\n\nlinks:\n${links.map((u) => `- ${u}`).join('\n')}`;
}

/**
 * The create request, as the BFF reads it.
 *
 * Files ride along only on an `agent-chat` automation: it is the only capability that runs the agent,
 * and nothing else on the other side would ever read them. An empty list writes no key at all — an
 * automation with no files must stay byte-identical to what this window sent before it could carry any.
 */
export function buildCreateBody(input: {
  capability: string;
  isLinkedIn: boolean;
  prompt: string;
  links: readonly string[];
  skillIds: readonly string[];
  attachments: readonly AutomationAttachment[];
  scheduleText?: string | null;
  runOnHolidays?: boolean;
}): {
  capability: string;
  payload: { prompt: string } & Record<string, unknown>;
} {
  const carriesFiles =
    input.capability === AGENT_CHAT_CAPABILITY && input.attachments.length > 0;
  return {
    capability: input.capability,
    payload: {
      prompt: input.isLinkedIn
        ? composeLinkedInPrompt(input.prompt, input.links)
        : input.prompt.trim(),
      ...(input.skillIds.length > 0 ? { skill_ids: [...input.skillIds] } : {}),
      ...(carriesFiles ? { attachments: [...input.attachments] } : {}),
      ...(input.scheduleText ? { schedule_text: input.scheduleText } : {}),
      ...(input.runOnHolidays === true ? { run_on_holidays: true } : {}),
    },
  };
}

interface CreateAutomationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (id: string) => void;
}

export function CreateAutomationDialog({
  open,
  onOpenChange,
  onCreated,
}: CreateAutomationDialogProps): JSX.Element {
  // Default to the first preset so the dialog opens with a ready-made prompt.
  const [preset, setPreset] = useState(DEFAULT_PRESET.id);
  const [prompt, setPrompt] = useState(DEFAULT_PRESET.seedPrompt);
  const [schedule, setSchedule] = useState<AutomationSchedule | undefined>(
    undefined,
  );
  const [scheduleText, setScheduleText] = useState<string | null>(null);
  const schedulePicker = useRef<SchedulePickerHandle>(null);
  const [phase, setPhase] = useState<CreatePhase>('idle');
  /** Set for the whole of one click on Create, so a second click while it runs is ignored. */
  const submitting = useRef(false);
  /** Moves on every click, close and unmount, so a save the user walked away from never lands. */
  const attempt = useRef(0);
  const [promptError, setPromptError] = useState<string | null>(null);
  const [maxFiresError, setMaxFiresError] = useState<string | null>(null);
  const [runOnHolidays, setRunOnHolidays] = useState(false);
  // LinkedIn-only: the attached links plus the in-progress input and its inline validation error.
  const [links, setLinks] = useState<string[]>([]);
  const [linkInput, setLinkInput] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  // Run-limit: when limitOn is true, maxFiresInput is the count the user typed.
  const [limitOn, setLimitOn] = useState(false);
  const [maxFiresInput, setMaxFiresInput] = useState('1');
  const [skillIds, setSkillIds] = useState<string[]>([]);
  /** Workspace files the run will read. */
  const [attachments, setAttachments] = useState<AutomationAttachment[]>([]);
  const create = useCreateAutomation();
  const skillsField = useAutomationSkillsField({
    prompt,
    onPromptChange: setPrompt,
    skillIds,
    onSkillIdsChange: setSkillIds,
  });
  /**
   * Where the files are picked from. There is no per-automation workspace picker: the automation lands
   * in the workspace the user is in, so without one there is nothing to search and no button to show.
   */
  const selectedWorkspaceId = useUiStore((store) => store.selectedWorkspaceId);

  const isLinkedIn = preset === LINKEDIN_PRESET_ID;
  // Number (not parseInt): parseInt silently truncates "1.5"→1 and "1e3"→1, saving a surprising
  // limit. Number + isSafeInteger rejects fractional/garbage and accepts exponent forms correctly.
  const parsedMaxFires = Number(maxFiresInput);
  const maxFiresValid =
    !limitOn || (Number.isSafeInteger(parsedMaxFires) && parsedMaxFires >= 1);

  // Clear the LinkedIn link editor state whenever the type changes or the dialog resets, so stale
  // links never leak across a type switch. (Reopen is handled by the close effect below.)
  function clearLinkState(): void {
    setLinks([]);
    setLinkInput('');
    setLinkError(null);
  }

  function selectPreset(id: string): void {
    setPreset(id);
    clearLinkState();
    const p = presetById(id);
    if (!p) return;
    setPrompt(p.seedPrompt);
  }

  // Parse one or more URLs out of `raw` (whitespace/comma separated — handles a multi-url paste or a
  // single typed url) and append the valid, non-duplicate linkedin.com ones. Returns how many were
  // added so callers can decide whether to clear the input. Sets an inline error for invalid tokens.
  function commitLinks(raw: string): { addedCount: number } {
    const tokens = raw.split(/[\s,]+/).filter((t) => t.length > 0);
    if (tokens.length === 0) {
      // Empty submit: nothing to add, but clear any stale error so the message
      // doesn't linger over a now-blank field.
      setLinkError(null);
      return { addedCount: 0 };
    }
    const seen = new Set(links);
    const toAdd: string[] = [];
    let invalid = 0;
    for (const t of tokens) {
      if (!isLinkedInUrl(t)) {
        invalid += 1;
        continue;
      }
      if (seen.has(t)) continue; // skip dups vs already-added AND within this batch
      seen.add(t);
      toAdd.push(t);
    }
    if (toAdd.length > 0) setLinks((prev) => [...prev, ...toAdd]);
    if (toAdd.length === 0 && invalid > 0) {
      setLinkError(
        tokens.length === 1
          ? 'Enter a valid linkedin.com URL.'
          : 'No valid linkedin.com URLs found.',
      );
    } else if (invalid > 0) {
      setLinkError(
        `Added ${toAdd.length}; skipped ${invalid} invalid URL${invalid > 1 ? 's' : ''}.`,
      );
    } else {
      setLinkError(null);
    }
    return { addedCount: toAdd.length };
  }

  // Commit the current input and clear it only when something landed (invalid text is kept so the
  // user can fix it rather than silently lose it).
  function commitInput(): void {
    const { addedCount } = commitLinks(linkInput);
    if (addedCount > 0) setLinkInput('');
  }

  function removeLink(url: string): void {
    setLinks((prev) => prev.filter((u) => u !== url));
  }

  function reset(): void {
    setPreset(DEFAULT_PRESET.id);
    setPrompt(DEFAULT_PRESET.seedPrompt);
    setSchedule(undefined);
    setScheduleText(null);
    setPromptError(null);
    setMaxFiresError(null);
    setRunOnHolidays(false);
    clearLinkState();
    setLimitOn(false);
    setMaxFiresInput('1');
    setSkillIds([]);
    setAttachments([]);
  }

  // The dialog component stays mounted (only `open` toggles), so its state survives a close.
  // Reset on close so a cancelled dialog (X / esc / backdrop) never reopens with stale links,
  // prompt, or run-limit — the success path already resets before closing.
  useEffect(() => {
    if (!open) {
      reset();
      abandonSave();
    }
    // reset only calls stable setters; re-run solely when `open` flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(
    () => () => {
      attempt.current += 1;
    },
    [],
  );

  /** Forget the save in progress, if any: its answer is ignored and Create is ready again. */
  function abandonSave(): void {
    attempt.current += 1;
    submitting.current = false;
    setPhase('idle');
  }

  // Default schedule when the user never touches the picker (daily 09:00).
  const effectiveSchedule: AutomationSchedule = schedule ?? {
    cron: '0 9 * * *',
    tz: browserTz(),
  };

  /**
   * A required prompt for every type, at least one link for LinkedIn, and a whole run limit of at
   * least one when the limit is on. Each refusal is shown under its field, and focus moves to the first.
   */
  function fieldsValid(): boolean {
    const linkMessage = isLinkedIn && links.length === 0 ? LINK_REQUIRED : null;
    const checks = [
      {
        fieldId: PROMPT_FIELD_ID,
        message: prompt.trim() ? null : PROMPT_REQUIRED,
      },
      { fieldId: LINK_FIELD_ID, message: linkMessage },
      {
        fieldId: RUN_LIMIT_FIELD_ID,
        message: maxFiresValid ? null : RUN_LIMIT_INVALID,
      },
    ];
    setPromptError(checks[0]!.message);
    if (linkMessage !== null) setLinkError(linkMessage);
    setMaxFiresError(checks[2]!.message);
    return focusFirstInvalid(checks);
  }

  /**
   * Create never greys out. A click checks the fields, waits for the schedule's words to be
   * interpreted, then saves; a second click while that runs is ignored.
   */
  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (submitting.current || !fieldsValid()) return;
    submitting.current = true;
    const thisAttempt = ++attempt.current;
    try {
      setPhase('checking');
      const settled = (await schedulePicker.current?.settleForSave()) ?? {
        status: 'current',
      };
      if (attempt.current !== thisAttempt) return;
      if (settled.status !== 'current' && settled.status !== 'ready') return;
      setPhase('saving');
      await finishCreate.current(settled, thisAttempt);
    } finally {
      if (attempt.current === thisAttempt) {
        submitting.current = false;
        setPhase('idle');
      }
    }
  }

  /**
   * The request cannot be recalled once sent, but a dialog closed (and maybe reopened) since then is
   * no longer this save's: `thisAttempt` keeps the old answer from resetting or closing it.
   */
  /**
   * Runs from the latest render once the schedule is settled, so the fields are checked and sent as
   * they read now: anything typed while the schedule was checked is kept, not lost.
   */
  async function createIfValid(
    settled: SavableSchedule,
    thisAttempt: number,
  ): Promise<void> {
    if (fieldsValid()) await createWith(settled, thisAttempt);
  }
  const finishCreate = useLatest(createIfValid);

  async function createWith(
    settled: SavableSchedule,
    thisAttempt: number,
  ): Promise<void> {
    const ready = settled.status === 'ready' ? settled : null;
    try {
      const body = buildCreateBody({
        capability: AGENT_CHAT_CAPABILITY,
        isLinkedIn,
        prompt,
        links,
        skillIds,
        attachments,
        scheduleText: ready ? ready.scheduleText : scheduleText,
        runOnHolidays,
      });
      const created = await create.mutateAsync({
        capability: body.capability,
        schedule: ready ? ready.schedule : effectiveSchedule,
        payload: body.payload,
        ...(limitOn ? { maxFires: parsedMaxFires } : {}),
        ...(ready?.plan ? { plan: ready.plan } : {}),
      });
      if (attempt.current !== thisAttempt) return;
      notify.success(`“${created.name?.trim() || 'Scheduled'}” created`);
      reset();
      onOpenChange(false);
      onCreated?.(created.id);
    } catch {
      /** The api layer already surfaced a notify.error toast. */
    }
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Create"
      titleHidden
      showCloseButton={false}
      aria-labelledby="create-automation-title"
      className="flex max-h-[85vh] w-full max-w-[var(--dialog-w-md)] flex-col gap-0 overflow-hidden rounded-xl border-border/50 p-0 shadow-xl max-md:max-w-[calc(100%-2rem)]"
    >
      <div className="flex items-start justify-between gap-3 border-b border-border/40 px-4 py-4 md:px-6">
        <div>
          <h2
            id="create-automation-title"
            className="text-base font-semibold tracking-tight md:text-[1.0625rem]"
          >
            Create
          </h2>
          <p className="mt-0.5 max-w-md text-caption leading-snug text-muted-foreground">
            The prompt will run automatically on a schedule.
          </p>
          <div
            role="note"
            className="mt-2.5 flex max-w-md items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5"
          >
            <TriangleAlertIcon
              className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400"
              aria-hidden="true"
            />
            <p className="text-caption leading-relaxed text-foreground">
              This runs on its own, and nothing will ask you before it acts.
              Once created, it does whatever the task needs and your connectors
              allow — sending mail, changing files and records, even mailing a
              whole audience. Sent mail cannot be recalled. What it does is your
              responsibility.
            </p>
          </div>
        </div>
        <DialogClose asChild>
          <button
            type="button"
            aria-label="Close"
            className="flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
          >
            <XIcon className="size-4" />
          </button>
        </DialogClose>
      </div>

      <form
        onSubmit={handleSubmit}
        noValidate
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4 md:px-6">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="create-automation-type">Type</Label>
            <Select value={preset} onValueChange={selectPreset}>
              <SelectTrigger id="create-automation-type" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUTOMATION_PRESETS.map((opt) => (
                  <SelectItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <AutomationPromptField
            id={PROMPT_FIELD_ID}
            label="Prompt"
            value={prompt}
            onChange={(next) => {
              setPrompt(next);
              setPromptError(null);
            }}
            error={promptError}
            placeholder="What should the agent do?"
            skills={skillsField}
            attachments={attachments}
            onAttachmentsChange={setAttachments}
            workspaceId={selectedWorkspaceId}
          />

          {isLinkedIn && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={LINK_FIELD_ID}>LinkedIn links</Label>
              <div className="flex items-start gap-2">
                <div className="flex flex-1 flex-col gap-1">
                  <Input
                    id={LINK_FIELD_ID}
                    value={linkInput}
                    onChange={(e) => {
                      setLinkInput(e.target.value);
                      if (linkError) setLinkError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        commitInput();
                      }
                    }}
                    onBlur={() => {
                      if (linkInput.trim().length > 0) commitInput();
                    }}
                    onPaste={(e) => {
                      // Any paste (single bare url OR a multi-url blob) is treated as an add. We
                      // combine it with whatever's already typed so a partial prefix isn't lost,
                      // then parse the lot into chips. Valid → field clears; invalid → the text is
                      // left in the field (with an inline error) so the user can fix it.
                      const text = e.clipboardData.getData('text');
                      if (!text) return;
                      e.preventDefault();
                      const combined = `${linkInput} ${text}`;
                      const { addedCount } = commitLinks(combined);
                      setLinkInput(addedCount > 0 ? '' : combined.trim());
                    }}
                    placeholder="https://www.linkedin.com/in/…"
                    aria-invalid={linkError != null}
                    aria-describedby={
                      linkError ? fieldErrorId(LINK_FIELD_ID) : undefined
                    }
                  />
                  <FieldError fieldId={LINK_FIELD_ID} message={linkError} />
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={commitInput}
                >
                  Add
                </Button>
              </div>

              {links.length > 0 && (
                <div className="mt-1 flex flex-col gap-1.5">
                  <p className="text-caption font-medium text-foreground">
                    {links.length} link{links.length > 1 ? 's' : ''} added
                  </p>
                  <ul className="flex flex-col gap-1.5">
                    {links.map((url) => (
                      <li
                        key={url}
                        className="flex items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1.5"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <LinkIcon className="size-3.5 shrink-0 text-primary" />
                          <span className="truncate text-caption text-foreground">
                            {url}
                          </span>
                        </span>
                        <button
                          type="button"
                          aria-label={`Remove ${url}`}
                          onClick={() => removeLink(url)}
                          className="flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
                        >
                          <XIcon className="size-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-caption text-muted-foreground">
                Add one or more linkedin.com links — they're appended to the
                prompt on save.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="create-automation-limit-toggle"
                checked={limitOn}
                onCheckedChange={(v) => setLimitOn(v === true)}
              />
              <Label
                htmlFor="create-automation-limit-toggle"
                className="cursor-pointer font-normal"
              >
                Limit number of runs
              </Label>
            </div>
            {limitOn && (
              <div className="flex items-center gap-2 pl-6">
                <Input
                  id={RUN_LIMIT_FIELD_ID}
                  type="number"
                  min={1}
                  step={1}
                  value={maxFiresInput}
                  onChange={(e) => {
                    setMaxFiresInput(e.target.value);
                    setMaxFiresError(null);
                  }}
                  className="w-24"
                  aria-label="Maximum runs"
                  aria-invalid={maxFiresError !== null}
                  aria-describedby={
                    maxFiresError ? fieldErrorId(RUN_LIMIT_FIELD_ID) : undefined
                  }
                />
                <span className="text-sm text-muted-foreground">
                  runs maximum
                </span>
              </div>
            )}
            {limitOn && (
              <div className="pl-6">
                <FieldError
                  fieldId={RUN_LIMIT_FIELD_ID}
                  message={maxFiresError}
                />
              </div>
            )}
          </div>

          <SchedulePicker
            allowCustom
            value={schedule}
            onChange={setSchedule}
            scheduleText={scheduleText}
            onScheduleTextChange={setScheduleText}
            ref={schedulePicker}
            idPrefix="create-automation-schedule"
          />
          <div className="flex items-center gap-2">
            <Switch
              id="create-automation-holidays"
              checked={runOnHolidays}
              onCheckedChange={setRunOnHolidays}
            />
            <Label htmlFor="create-automation-holidays">
              Run on public holidays
            </Label>
          </div>
          <p className="text-caption text-muted-foreground">
            Otherwise, wait until your next working day at the same mailbox
            local time. Only the latest waiting run is kept. The run limit
            counts scheduled triggers, so fewer runs may complete. One-time and
            manual runs are exempt. A schedule worked out from your words runs
            on public holidays too. To skip them, say so in the words.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border/40 bg-muted/10 px-4 py-3 md:px-6">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            size="sm"
            aria-busy={phase !== 'idle'}
          >
            {phase === 'checking'
              ? 'Checking schedule…'
              : phase === 'saving' || create.isPending
                ? 'Creating…'
                : 'Create'}
          </Button>
        </div>
      </form>
    </AppDialog>
  );
}
